import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params, Router, UrlTree } from '@angular/router';

/** Shared PDF drilldown URL handling (legacy `app.js` `$urlRouterProvider.otherwise` + `#popup`). */
@Injectable({ providedIn: 'root' })
export class FormViewDrilldownRoutingService {
  static readonly PENDING_DRILLDOWN_KEY = 'gtw-form-view-pending-drilldown';

  private static readonly DRILLDOWN_URL_PARAM_KEYS = [
    'element_name',
    'schedule',
    'line',
    'col_id',
    'line_total',
    'table_name',
    'partNo',
    'part_no',
  ];

  private globalGuardsInstalled = false;

  constructor(private router: Router) {}

  /**
   * Parse PDF drilldown params from legacy URL shapes:
   * - `#popup?element_name=...`
   * - `#/form-view/dashboard?element_name=...`
   * - `#/home?element_name=...` (PDF engine stray deep link)
   */
  parseDrilldownParamsFromUrl(url: string): Record<string, string> | null {
    if (!url) {
      return null;
    }

    const hasPopup = url.includes('popup');
    const hasElementName = /(?:\?|&)element_name=/i.test(url);
    if (!hasPopup && !hasElementName) {
      return null;
    }

    let queryPart = '';
    const hashIdx = url.indexOf('#');
    const hash = hashIdx >= 0 ? url.substring(hashIdx) : '';
    if (hash.includes('?')) {
      queryPart = hash.substring(hash.indexOf('?') + 1);
    } else if (url.includes('?')) {
      queryPart = url.substring(url.indexOf('?') + 1);
    }

    if (!queryPart) {
      return null;
    }

    const params: Record<string, string> = {};
    queryPart.split('&').forEach(segment => {
      const eq = segment.indexOf('=');
      if (eq < 0) {
        return;
      }
      const key = segment.substring(0, eq);
      const val = segment.substring(eq + 1);
      if (key) {
        params[key] = val === 'null' ? '' : decodeURIComponent(val || '');
      }
    });

    if (hasPopup || params['element_name']) {
      return params;
    }
    return null;
  }

  mergeDrilldownParams(route: ActivatedRouteSnapshot): Record<string, string> | null {
    const fromRoute: Record<string, string> = {};
    const q = route.queryParams || {};
    Object.keys(q).forEach(k => {
      if (q[k] !== undefined && q[k] !== null) {
        fromRoute[k] = String(q[k]);
      }
    });

    const fromHash = this.parseDrilldownParamsFromUrl(
      typeof window !== 'undefined' ? window.location.href : ''
    );

    const merged = { ...(fromHash || {}), ...fromRoute };
    if (Object.keys(merged).length === 0) {
      return null;
    }
    if (merged['element_name'] || route.routeConfig?.path === 'popup') {
      return merged;
    }
    return null;
  }

  getDefaultFormViewRoutePath(isState = false): string {
    return isState ? '/state-form-view/dashboard' : '/form-view/dashboard';
  }

  /** Legacy `formViewTabCtrl.addTabData`: `leName + '-' + elementName`. */
  buildTabCode(leName: string, elementName: string): string {
    return `${leName || ''}-${elementName || ''}`;
  }

  parseElementViewTabId(routerUrl: string, isState = false): string | null {
    const path = (routerUrl || '').split('?')[0];
    const prefix = isState ? '/state-form-view/' : '/form-view/';
    if (!path.startsWith(prefix)) {
      return null;
    }
    const segment = path.substring(prefix.length);
    if (!segment || segment === 'dashboard') {
      return null;
    }
    return decodeURIComponent(segment);
  }

  isElementViewRouterUrl(routerUrl: string, isState = false): boolean {
    return !!this.parseElementViewTabId(routerUrl, isState);
  }

  sanitizeDrilldownReturnRoute(route: string, isState = false): string {
    const pathOnly = (route || '')
      .split('?')[0]
      .replace(/^#/, '')
      .replace(/^\//, '')
      .toLowerCase();

    if (pathOnly === 'home' || pathOnly === 'popup') {
      return this.getDefaultFormViewRoutePath(isState);
    }
    if (pathOnly === 'form-view/dashboard' || pathOnly === 'state-form-view/dashboard') {
      return `/${pathOnly}`;
    }
    if (
      pathOnly.startsWith('form-view/')
      && pathOnly !== 'form-view/dashboard'
    ) {
      return `/${pathOnly}`;
    }
    if (
      pathOnly.startsWith('state-form-view/')
      && pathOnly !== 'state-form-view/dashboard'
    ) {
      return `/${pathOnly}`;
    }
    return this.getDefaultFormViewRoutePath(isState);
  }

  createDashboardUrlTree(isState = false): UrlTree {
    const segments = isState
      ? ['state-form-view', 'dashboard']
      : ['form-view', 'dashboard'];
    return this.router.createUrlTree(segments, {
      queryParams: { client_key: sessionStorage.getItem('client_key') || '0' },
    });
  }

  /** Legacy `form-view.element-view` URL: `#/form-view/AL8301-IRS1065.OwedAmt?client_key=0`. */
  createElementViewUrlTree(tabCode: string, isState = false): UrlTree {
    const segments = isState
      ? ['state-form-view', tabCode]
      : ['form-view', tabCode];
    return this.router.createUrlTree(segments, {
      queryParams: { client_key: sessionStorage.getItem('client_key') || '0' },
    });
  }

  buildFormViewUrlWithDrilldownParams(
    drilldownParams: Record<string, string>,
    options: { returnRoute?: string; isState?: boolean } = {}
  ): string {
    const isState = options.isState === true;
    const base = this.sanitizeDrilldownReturnRoute(
      options.returnRoute || this.getDefaultFormViewRoutePath(isState),
      isState
    );
    const q = new URLSearchParams();
    const clientKey =
      drilldownParams['client_key']
      || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('client_key') : null)
      || '0';
    q.set('client_key', clientKey);

    FormViewDrilldownRoutingService.DRILLDOWN_URL_PARAM_KEYS.forEach(key => {
      const value = drilldownParams[key];
      if (value !== undefined && value !== null && String(value) !== '') {
        q.set(key, String(value));
      }
    });

    return `${base}?${q.toString()}`;
  }

  createFormViewDrilldownUrlTree(
    drilldownParams: Record<string, string>,
    options: { returnRoute?: string; isState?: boolean } = {}
  ): UrlTree {
    const full = this.buildFormViewUrlWithDrilldownParams(drilldownParams, options);
    const [path, query] = full.split('?');
    const queryParams: Params = {};
    if (query) {
      new URLSearchParams(query).forEach((v, k) => {
        queryParams[k] = v;
      });
    }
    return this.router.createUrlTree([path.startsWith('/') ? path : `/${path}`], { queryParams });
  }

  queuePendingDrilldown(
    params: Record<string, string>,
    options: { returnRoute?: string; isState?: boolean } = {}
  ): void {
    try {
      sessionStorage.setItem(
        FormViewDrilldownRoutingService.PENDING_DRILLDOWN_KEY,
        JSON.stringify({
          params,
          returnRoute: options.returnRoute || this.getDefaultFormViewRoutePath(options.isState),
          isState: options.isState === true,
        })
      );
    } catch (_e) {
      // Ignore quota errors.
    }
  }

  readPendingDrilldown(): {
    params: Record<string, string>;
    returnRoute?: string;
    isState?: boolean;
  } | null {
    try {
      const raw = sessionStorage.getItem(FormViewDrilldownRoutingService.PENDING_DRILLDOWN_KEY) || '';
      if (!raw) {
        return null;
      }
      const pending = JSON.parse(raw);
      if (pending?.params) {
        return pending;
      }
    } catch (_e) {
      // Ignore malformed payload.
    }
    return null;
  }

  clearPendingDrilldown(): void {
    try {
      sessionStorage.removeItem(FormViewDrilldownRoutingService.PENDING_DRILLDOWN_KEY);
    } catch (_e) {
      // Ignore.
    }
  }

  inferIsStateFromContext(): boolean {
    const href = typeof window !== 'undefined' ? window.location.href.toLowerCase() : '';
    if (href.includes('state-form-view')) {
      return true;
    }
    const path = (this.router.url || '').toLowerCase();
    return path.includes('state-form-view');
  }

  rememberReturnRouteFromUrl(fullUrl: string, isState: boolean): string {
    if (!fullUrl) {
      return this.getDefaultFormViewRoutePath(isState);
    }
    const hashIdx = fullUrl.indexOf('#/');
    if (hashIdx < 0) {
      return this.getDefaultFormViewRoutePath(isState);
    }
    const route = fullUrl.substring(hashIdx + 1).split('?')[0];
    if (route && !route.toLowerCase().includes('popup')) {
      return this.sanitizeDrilldownReturnRoute(
        route.startsWith('/') ? route : `/${route}`,
        isState
      );
    }
    return this.getDefaultFormViewRoutePath(isState);
  }

  /** True when running on localhost (local shell, APIs often proxied to stage/dev). */
  isLocalDevHost(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const host = (window.location?.hostname || '').toLowerCase();
    return host === 'localhost' || host === '127.0.0.1' || host === '::1';
  }

  /**
   * Legacy element-view path in hash: `#/form-view/CC2005-IRS1065.OtherDeductionsAmt?...`
   * (not `dashboard` or transient `popup`).
   */
  private isFormViewElementViewHash(hash: string): boolean {
    const normalized = (hash || '').replace(/^#/, '').split('?')[0];
    const match = normalized.match(/^\/?(state-form-view|form-view)\/([^/]+)$/i);
    if (!match) {
      return false;
    }
    const segment = (match[2] || '').toLowerCase();
    return segment !== 'dashboard' && segment !== 'popup';
  }

  /**
   * URLs that must stay on the current origin during local dev (PDF engine often
   * embeds absolute `https://stage.gtw.pwc.com/...` links).
   */
  isDrilldownRelatedNavigation(url: string): boolean {
    const raw = String(url || '');
    if (!raw) {
      return false;
    }
    if (raw.includes('popup') || /(?:\?|&)element_name=/i.test(raw)) {
      return true;
    }
    const hashIdx = raw.indexOf('#');
    if (hashIdx >= 0) {
      const hash = raw.substring(hashIdx);
      if (this.isFormViewElementViewHash(hash)) {
        return true;
      }
    }
    try {
      if (typeof window !== 'undefined') {
        const parsed = new URL(raw, window.location.href);
        if (parsed.hash && this.isFormViewElementViewHash(parsed.hash)) {
          return true;
        }
      }
    } catch (_e) {
      // Ignore invalid URL.
    }
    return false;
  }

  /**
   * Rewrite absolute stage/dev drilldown navigations to the current origin so local
   * dev stays on http://localhost:4200 while APIs proxy to stage/dev.
   *
   * Handles:
   * - `#popup?element_name=...` (hover preview in status bar)
   * - `#/form-view/{leName}-{elementName}?client_key=...` (final legacy URL)
   */
  rewritePopupNavigation(url: string): string {
    try {
      const raw = String(url || '');
      if (!this.isDrilldownRelatedNavigation(raw)) {
        return raw;
      }
      if (typeof window === 'undefined') {
        return raw;
      }
      const parsed = new URL(raw, window.location.href);
      if (parsed.origin === window.location.origin) {
        return raw;
      }
      const path = window.location.pathname || '/';
      const search = window.location.search || '';
      const hash = parsed.hash || '';
      if (hash) {
        return `${window.location.origin}${path}${search}${hash}`;
      }
      const query = parsed.search || (raw.includes('?') ? '?' + raw.split('?').slice(1).join('?') : '');
      if (raw.includes('popup')) {
        return `${window.location.origin}${path}${search}#popup${query.startsWith('?') ? query : '?' + query.replace(/^\?/, '')}`;
      }
      return `${window.location.origin}${path}${search}${parsed.pathname}${query}`;
    } catch (_e) {
      return url;
    }
  }

  /**
   * PDF viewers inside blob iframes often set `parent.location.href` / `top.location.href`
   * (not assign/replace). Patch the href setter so stage hosts are rewritten locally.
   */
  private patchLocationHrefSetter(): void {
    if (!this.isLocalDevHost() || typeof window === 'undefined') {
      return;
    }
    const proto = window.Location?.prototype as Location & { __gtwDrilldownHrefPatched?: boolean };
    if (!proto || proto.__gtwDrilldownHrefPatched) {
      return;
    }
    const descriptor = Object.getOwnPropertyDescriptor(proto, 'href');
    if (!descriptor?.set || !descriptor?.get) {
      return;
    }
    const service = this;
    const nativeSet = descriptor.set;
    const nativeGet = descriptor.get;
    Object.defineProperty(proto, 'href', {
      configurable: true,
      enumerable: descriptor.enumerable ?? true,
      get(): string {
        return nativeGet.call(this);
      },
      set(value: string): void {
        nativeSet.call(this, service.rewritePopupNavigation(String(value)));
      },
    });
    proto.__gtwDrilldownHrefPatched = true;
  }

  /**
   * Legacy `app.js`: when hash contains `popup`, stay on current route — do not fall through to home/modules.
   * Installs location hooks + link capture so PDF drilldowns do not hard-navigate to stage host.
   */
  installGlobalGuards(): void {
    if (this.globalGuardsInstalled || typeof window === 'undefined') {
      return;
    }
    this.globalGuardsInstalled = true;

    const w = window as any;
    if (!w.__gtwFormViewDrilldownGlobalGuards) {
      w.__gtwFormViewDrilldownGlobalGuards = true;

      try {
        const loc = window.location;
        const originalReplace = loc.replace.bind(loc);
        const originalAssign = loc.assign.bind(loc);
        loc.replace = (url: string | URL) => {
          originalReplace(this.rewritePopupNavigation(String(url)));
        };
        loc.assign = (url: string | URL) => {
          originalAssign(this.rewritePopupNavigation(String(url)));
        };
        this.patchLocationHrefSetter();
      } catch (_e) {
        // Some browsers block Location patching.
      }

      const originalOpen = window.open.bind(window);
      window.open = (
        url?: string | URL,
        target?: string,
        features?: string
      ): Window | null => {
        if (url !== undefined && url !== null && String(url) !== '') {
          return originalOpen(this.rewritePopupNavigation(String(url)), target, features);
        }
        return originalOpen(undefined, target, features);
      };

      document.addEventListener(
        'click',
        (event: MouseEvent) => {
          const target = event.target as HTMLElement | null;
          const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
          if (!anchor) {
            return;
          }
          const href = anchor.getAttribute('href') || '';
          if (!this.isDrilldownRelatedNavigation(href)) {
            return;
          }
          try {
            const parsed = new URL(href, window.location.href);
            if (parsed.origin === window.location.origin) {
              return;
            }
          } catch (_e) {
            return;
          }
          const rewritten = this.rewritePopupNavigation(href);
          event.preventDefault();
          event.stopPropagation();
          if (rewritten.includes('#')) {
            const hashIdx = rewritten.indexOf('#');
            window.location.hash = rewritten.substring(hashIdx);
          } else {
            window.location.assign(rewritten);
          }
        },
        true
      );
    }

    const onHashChange = (): void => {
      const href = window.location.href || '';
      const params = this.parseDrilldownParamsFromUrl(href);
      if (!params) {
        return;
      }
      const path = (this.router.url || '').split('?')[0].toLowerCase();
      if (path.includes('/form-view') || path.includes('/state-form-view')) {
        return;
      }
      const isState = this.inferIsStateFromContext();
      const returnRoute = this.rememberReturnRouteFromUrl(href, isState);
      this.queuePendingDrilldown(params, { returnRoute, isState });
      void this.router.navigateByUrl(this.createDashboardUrlTree(isState), {
        replaceUrl: true,
      });
    };

    window.addEventListener('hashchange', onHashChange);
  }
}
